// Agent detail page. Header + stats + controls + per-site breakdown
// + paginated action log.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { agentsApi, type AgentTrustPhase, type AgentAction } from "../lib/api";
import { AgentAvatar } from "../components/AgentAvatar";
import { LiveActivityCard } from "../components/LiveActivityCard";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

export function AgentDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [showHeartbeats, setShowHeartbeats] = useState(true);
  const q = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.detail(id),
    refetchInterval: 15_000,
    enabled: Boolean(id),
  });

  const enableM = useMutation({
    mutationFn: (action: "enable" | "disable") => agentsApi.control(id, { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent", id] }),
  });
  const phaseM = useMutation({
    mutationFn: (trustPhase: AgentTrustPhase) =>
      agentsApi.control(id, { action: "set-phase", trustPhase }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent", id] }),
  });
  const runNowM = useMutation({
    mutationFn: () => agentsApi.runNow(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent", id] }),
  });

  return (
    <div className="space-y-6">
      <Link to="/agents" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-900 no-underline">
        ← All agents
      </Link>

      {q.isLoading && <div className="text-sm text-ink-500">Loading…</div>}
      {q.error && <div className="text-sm text-red-700">Failed: {String((q.error as Error).message)}</div>}
      {q.data && (
        <>
          <div className="bg-white rounded-xl border border-ink-100 p-6">
            <div className="flex items-start gap-4">
              <AgentAvatar
                id={q.data.agent.id}
                displayName={q.data.agent.displayName}
                emoji={q.data.agent.emoji}
                url={q.data.agent.avatarUrl}
                size={72}
              />
              <div className="flex-1">
                <div className="flex items-baseline gap-2 mb-1">
                  <h1 className="text-2xl font-display font-bold">{q.data.agent.displayName}</h1>
                  <code className="text-xs text-ink-400">v{q.data.agent.version} · {q.data.agent.id}</code>
                </div>
                <p className="text-ink-700 mb-4">{q.data.agent.role}</p>
                <div className="flex items-center gap-3 flex-wrap text-sm">
                  <button
                    onClick={() => enableM.mutate(q.data!.agent.enabled ? "disable" : "enable")}
                    disabled={enableM.isPending}
                    className={`px-3 py-1.5 rounded-md font-medium ${
                      q.data.agent.enabled
                        ? "bg-red-50 text-red-800 border border-red-200 hover:bg-red-100"
                        : "bg-green-50 text-green-800 border border-green-200 hover:bg-green-100"
                    }`}
                  >
                    {q.data.agent.enabled ? "Disable" : "Enable"}
                  </button>
                  <button
                    onClick={() => runNowM.mutate()}
                    disabled={!q.data.agent.enabled || runNowM.isPending}
                    title={
                      !q.data.agent.enabled
                        ? "Enable the agent first"
                        : "Sets next_run_at = NOW() + dispatches a quickping. Tick fires within ~30-90s."
                    }
                    className="px-3 py-1.5 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    {runNowM.isPending ? "Dispatching…" : "▶ Run now"}
                  </button>
                  {runNowM.isSuccess && runNowM.data?.runsUrl && (
                    <a
                      href={runNowM.data.runsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-700 underline hover:text-blue-900"
                    >
                      Watch run ↗
                    </a>
                  )}
                  {runNowM.isError && (
                    <span className="text-xs text-red-700">
                      {String((runNowM.error as Error).message)}
                    </span>
                  )}
                  <div className="flex items-center gap-1 border border-ink-200 rounded-md p-0.5">
                    {(["dry_run", "confirm", "autonomous"] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => phaseM.mutate(p)}
                        disabled={q.data!.agent.trustPhase === p || phaseM.isPending}
                        className={`px-2.5 py-1 text-xs rounded ${
                          q.data!.agent.trustPhase === p
                            ? "bg-ink-900 text-white"
                            : "text-ink-700 hover:bg-ink-50"
                        }`}
                      >
                        {p === "dry_run" ? "dry-run" : p}
                      </button>
                    ))}
                  </div>
                  {q.data.agent.runbookPath && (
                    <code className="text-xs text-ink-500 bg-ink-50 px-2 py-1 rounded">
                      📖 {q.data.agent.runbookPath}
                    </code>
                  )}
                </div>
                {/* Schedule status — last + next run, refreshes with the
                 *  outer agent query (15s). Inline pill keeps it tight
                 *  next to the controls so operators see whether their
                 *  Run-now click took effect.  */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-600">
                  <span>
                    <span className="text-ink-500">Last run:</span>{" "}
                    <span className="tabular-nums">
                      {q.data.agent.lastRunAt ? relativeTime(q.data.agent.lastRunAt) : "never"}
                    </span>
                  </span>
                  <span>
                    <span className="text-ink-500">Next run:</span>{" "}
                    <span className={`tabular-nums ${nextRunHighlight(q.data.agent.nextRunAt)}`}>
                      {q.data.agent.nextRunAt
                        ? relativeTimeFuture(q.data.agent.nextRunAt)
                        : "—"}
                    </span>
                  </span>
                  <span>
                    <span className="text-ink-500">Cadence:</span>{" "}
                    <span className="tabular-nums">{formatInterval(q.data.agent.intervalSec)}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile label="Trust" value={q.data.agent.trustPhase === "dry_run" ? "dry-run" : q.data.agent.trustPhase} />
            <Tile label="Hourly" value={`${q.data.agent.stats.actionsLastHour} / ${q.data.agent.hourlyBudget}`} />
            <Tile label="24h" value={`${q.data.agent.stats.actionsLast24h} / ${q.data.agent.dailyBudget}`} />
            <Tile
              label="24h failed"
              value={q.data.agent.stats.failed24h}
              tone={q.data.agent.stats.failed24h > 0 ? "red" : undefined}
            />
          </div>

          {/* Live activity (auto-polls every 5s; surfaces in-progress
              tick state from the agent's domain tables — for Spencer
              that's serp_snapshots + competitor_authority counts). */}
          <LiveActivityCard agentId={id} />

          {/* Per-site breakdown */}
          {q.data.perSite.length > 0 && (
            <PerSiteTable rows={q.data.perSite} />
          )}

          {/* Action log */}
          <div className="bg-white rounded-xl border border-ink-100 p-5">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
                Recent actions ({q.data.actions.length})
              </h2>
              <label className="text-xs text-ink-500 flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showHeartbeats}
                  onChange={(e) => setShowHeartbeats(e.target.checked)}
                  className="cursor-pointer"
                />
                Show heartbeats
              </label>
            </div>
            {q.data.actions.length === 0 ? (
              <p className="text-sm text-ink-500">No actions yet. Run <code className="bg-ink-100 px-1 rounded">rankforge agents run {id} --force</code> to test.</p>
            ) : (
              <ActionsTable actions={q.data.actions} showHeartbeats={showHeartbeats} agentId={id} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PerSiteTable({ rows }: { rows: Array<{ siteId: string | null; domain: string | null; actionCount: number; okCount: number; failedCount: number; lastAt: string | null }> }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable(
    rows,
    {
      site: (s) => s.domain,
      total: (s) => s.actionCount,
      ok: (s) => s.okCount,
      failed: (s) => s.failedCount,
      last: (s) => s.lastAt,
    },
    { key: "total", dir: "desc" },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-1">
        Most active sites
      </h2>
      <p className="text-[11px] text-ink-500 mb-3">
        All-time totals from this agent's history. Sorted by total agent_action rows per site.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
            <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2" />
            <SortHeader label="Total" sortKey="total" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
            <SortHeader label="Ok" sortKey="ok" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
            <SortHeader label="Failed" sortKey="failed" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
            <SortHeader label="Last action" sortKey="last" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((s) => (
            <tr key={s.siteId ?? "—"} className="border-b border-ink-50">
              <td className="py-2">
                {s.siteId ? (
                  <Link to={`/sites/${s.siteId}`} className="text-ink-900 hover:text-accent">
                    {s.domain}
                  </Link>
                ) : (
                  <span className="text-ink-400">—</span>
                )}
              </td>
              <td className="py-2 text-right tabular-nums">{s.actionCount}</td>
              <td className="py-2 text-right tabular-nums text-green-700">{s.okCount}</td>
              <td className="py-2 text-right tabular-nums text-red-700">{s.failedCount}</td>
              <td className="py-2 text-right text-xs text-ink-500">{s.lastAt ? relTime(s.lastAt) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionsTable({ actions, showHeartbeats, agentId: _agentId }: { actions: AgentAction[]; showHeartbeats: boolean; agentId: string }) {
  const filtered = actions.filter((a) => showHeartbeats || a.actionKind !== "tick_summary");
  // Surface a per-row "Site / Alert" sortable value; heartbeats have no
  // site, so they sort to the bottom whatever the column. Detail column
  // is left plain because it's a free-form blob (different shape per
  // action_kind) and there's no meaningful single sortable value.
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<AgentAction>(
    filtered,
    {
      time: (a) => a.startedAt,
      action: (a) => a.actionKind,
      outcome: (a) => a.outcome,
      site: (a) => a.siteId,
    },
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
            <SortHeader label="Time" sortKey="time" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2" />
            <SortHeader label="Action" sortKey="action" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2" />
            <SortHeader label="Outcome" sortKey="outcome" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2" />
            <SortHeader label="Site / Alert" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2" />
            <th className="pb-2">Detail</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((a) => (
            a.actionKind === "tick_summary"
              ? <HeartbeatRow key={a.id} action={a} />
              : <ActionRow key={a.id} action={a} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionRow({ action: a }: { action: AgentAction }) {
  return (
    <tr className="border-b border-ink-50 align-top">
      <td className="py-2 text-xs text-ink-500 tabular-nums whitespace-nowrap pr-3">
        {relTime(a.startedAt)}
        {a.dryRun && <span className="ml-1 text-blue-600">[dry]</span>}
      </td>
      <td className="py-2 text-xs font-mono pr-3">{a.actionKind}</td>
      <td className="py-2 pr-3">
        <OutcomeBadge outcome={a.outcome} />
      </td>
      <td className="py-2 text-xs pr-3">
        {a.siteId ? (
          <Link to={`/sites/${a.siteId}`} className="text-ink-700 hover:text-accent">
            {a.siteId.slice(0, 8)}…
          </Link>
        ) : <span className="text-ink-400">—</span>}
        {a.alertId && <span className="text-ink-400 ml-1">· alert {a.alertId.slice(0, 6)}…</span>}
      </td>
      <td className="py-2 text-xs text-ink-600 max-w-md break-words">
        {a.errorMessage
          ? <span className="text-red-700">{a.errorMessage}</span>
          : (summarizeInputState(a.actionKind, a.inputState) ?? "—")}
      </td>
    </tr>
  );
}

/**
 * Reduce an action's inputState blob to a one-line summary suitable
 * for the Detail column on the AgentDetailPage. Returns null when
 * nothing useful is in there. Per-actionKind format because each
 * agent emits different shapes — this is the central registry.
 */
function summarizeInputState(actionKind: string, raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  const num = (k: string): number | null => (typeof s[k] === "number" ? s[k] as number : null);
  const str = (k: string): string | null => (typeof s[k] === "string" ? s[k] as string : null);

  switch (actionKind) {
    case "discover-keywords": {
      const inserted = num("inserted") ?? 0;
      const fetched = num("fetched") ?? 0;
      const skipped = num("skipped") ?? 0;
      const reason = str("triggerReason");
      const dom = str("siteDomain");
      const trigger = reason ? ` · trigger: ${reason}` : "";
      const dest = dom ? `${dom} · ` : "";
      // Render skip-reason breakdown when present so the operator
      // can see whether filtering rejected as duplicate vs off-topic
      // vs low_volume etc. Hide zero counts to keep it short.
      let skipDetail = "";
      const reasons = s.skipReasons;
      if (reasons && typeof reasons === "object") {
        const parts: string[] = [];
        for (const [k, v] of Object.entries(reasons as Record<string, unknown>)) {
          if (typeof v === "number" && v > 0) parts.push(`${v} ${k}`);
        }
        if (parts.length > 0) skipDetail = ` (${parts.join(" / ")})`;
      }
      return `${dest}${inserted} new · ${fetched} fetched · ${skipped} filtered${skipDetail}${trigger}`;
    }
    case "fast-drain-discovered":
    case "post-execute-capture": {
      const count = num("count") ?? 0;
      const sites = num("sites") ?? 0;
      const failures = num("failures") ?? 0;
      const quotaExhausted = s.quotaExhausted === true;
      // Pull the probed-items sample for inline rendering. Each item:
      // {domain, keyword, position, localPack}.
      const probedItems = Array.isArray(s.probedItems) ? (s.probedItems as Array<Record<string, unknown>>) : [];
      const probeLines = probedItems.slice(0, 8).map((p) => {
        const dom = typeof p.domain === "string" ? p.domain : "?";
        const kw = typeof p.keyword === "string" ? p.keyword : "?";
        const pos = typeof p.position === "number" ? `#${p.position}` : "n/r";
        const lp = p.localPack === true ? " · LP" : "";
        return `${dom} "${kw}" → ${pos}${lp}`;
      });
      const more = probedItems.length > 8 ? ` (+${probedItems.length - 8} more)` : "";
      const headline = quotaExhausted
        ? `⚠ DataForSEO quota exhausted — 0 probed · ${failures} failed`
        : `probed ${count} pair(s) across ${sites} site(s)${failures > 0 ? ` · ${failures} failed` : ""}`;
      // If we have probed items, render them as a multi-line tooltip
      // hint AND a one-line preview ("3 ranked, 5 not ranked").
      if (probedItems.length > 0) {
        const ranked = probedItems.filter((p) => typeof p.position === "number").length;
        const nr = probedItems.length - ranked;
        return `${headline} · ${ranked} ranked, ${nr} n/r${more} — ${probeLines.slice(0, 3).join(" · ")}${probeLines.length > 3 ? " · …" : ""}`;
      }
      return headline;
    }
    case "competitor-probe": {
      const dom = str("domain");
      const kw = num("keywordCount") ?? 0;
      const locals = num("distinctLocals") ?? 0;
      const diff = str("difficultyLabel");
      return `${dom ?? "?"} · ${kw} kws · ${locals} local · ${diff ?? "?"}`;
    }
    case "competitor-portfolio-rollup": {
      const sitesProbed = num("sitesProbed") ?? 0;
      return `${sitesProbed} site(s) — portfolio difficulty rollup`;
    }
    case "content-brief": {
      const dom = str("domain");
      const kw = str("keyword");
      return dom && kw ? `${dom} · "${kw}"` : null;
    }
    case "add-internal-link": {
      const dom = str("domain");
      const anchor = str("anchorText");
      return dom && anchor ? `${dom} · anchor: "${anchor}"` : null;
    }
    case "grade": {
      const dom = str("domain");
      const grade = str("grade");
      const pct = num("score");
      return dom && grade ? `${dom} · graded ${grade}${pct != null ? ` (${pct}%)` : ""}` : null;
    }
  }

  // Generic fallback: surface "domain" or "siteDomain" if present —
  // most actions include one or the other and it's the most useful
  // signal of "what site got worked on."
  const dom = str("domain") ?? str("siteDomain");
  return dom ?? null;
}

function HeartbeatRow({ action: a }: { action: AgentAction }) {
  // tick_summary rows write {proposed, applied, failed, skipped,
  // durationMs, logLines, trustPhase} into inputState.
  const s = (a.inputState ?? {}) as {
    proposed?: number; applied?: number; failed?: number; skipped?: number;
    durationMs?: number; logLines?: string[]; trustPhase?: string;
  };
  const counts: string[] = [];
  if ((s.proposed ?? 0) > 0) counts.push(`${s.proposed} proposed`);
  if ((s.applied ?? 0) > 0) counts.push(`${s.applied} applied`);
  if ((s.failed ?? 0) > 0) counts.push(`${s.failed} failed`);
  if ((s.skipped ?? 0) > 0) counts.push(`${s.skipped} skipped`);
  const summary = counts.length ? counts.join(" · ") : "no work this tick";
  return (
    <tr className="border-b border-ink-50 align-top bg-ink-50/40">
      <td className="py-2 text-xs text-ink-500 tabular-nums whitespace-nowrap pr-3">
        {relTime(a.startedAt)}
      </td>
      <td className="py-2 pr-3 text-xs text-ink-500">
        <span className="inline-flex items-center gap-1">
          <span className="text-ink-400">♥</span>
          tick
          {typeof s.durationMs === "number" && (
            <span className="text-ink-400">· {s.durationMs}ms</span>
          )}
        </span>
      </td>
      <td className="py-2 pr-3 text-xs text-ink-500">{summary}</td>
      <td className="py-2 pr-3 text-xs text-ink-400">—</td>
      <td className="py-2 text-xs text-ink-500 max-w-md break-words">
        {s.logLines && s.logLines.length > 0 ? (
          <details>
            <summary className="cursor-pointer hover:text-ink-700">
              {s.logLines[0]}
              {s.logLines.length > 1 && <span className="text-ink-400"> +{s.logLines.length - 1} more</span>}
            </summary>
            <pre className="mt-1 text-[11px] whitespace-pre-wrap text-ink-600 leading-tight">
              {s.logLines.join("\n")}
            </pre>
          </details>
        ) : "—"}
      </td>
    </tr>
  );
}

function Tile({ label, value, tone }: { label: string; value: string | number; tone?: "red" }) {
  const cls = tone === "red"
    ? "bg-red-50 border-red-200 text-red-800"
    : "bg-ink-50 border-ink-200 text-ink-900";
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <div className="text-2xl font-display font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const cls: Record<string, string> = {
    ok: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-700",
    escalated: "bg-amber-100 text-amber-800",
    dry_run: "bg-blue-100 text-blue-800",
    skipped: "bg-ink-100 text-ink-500",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-medium ${cls[outcome] ?? "bg-ink-100 text-ink-500"}`}>
      {outcome}
    </span>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

const relativeTime = relTime;

function relativeTimeFuture(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "due now";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `in ${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `in ${hr}h`;
  const d = Math.round(hr / 24);
  return `in ${d}d`;
}

function nextRunHighlight(iso: string | null): string {
  if (!iso) return "text-ink-400";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "text-amber-700 font-medium"; // due now or overdue
  return "text-ink-700";
}

function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.round(hr / 24);
  return `${d}d`;
}
